import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getCookie, setCookie } from "../common/utils/cookie";
import { isLocalDev } from "../common/utils/env";

export enum FeatureName {
  // Typescript gets angry if the enum is empty
  TEMP_PLACEHOLDER = "temp-placeholder",
  NEW_AI_USER_FLOW = "new-ai-user-flow",
}

type FeatureValue = boolean; // Could be extended to string/number/etc
type FeatureMap = Record<FeatureName, FeatureValue>;
interface FeatureContextShape {
  allFeatures: FeatureMap;
  myFeatures: Partial<FeatureMap>;
  setFeature: (key: FeatureName, value: FeatureValue) => void;
  resetFeature: (key: FeatureName) => void;
}

// Currently doing opt-in-only features, so we can programmatically
// default everything to false.
const featureDefaults: FeatureMap = Object.values(FeatureName).reduce(
  (obj, val) => ({
    ...obj,
    [val]: false,
  }),
  {} as FeatureMap
);

const COOKIE_NAME = "supernova-features";
const cookieVal = getCookie(COOKIE_NAME);
const storedFeatures = cookieVal ? JSON.parse(cookieVal) : {};

const FeatureContext = React.createContext<FeatureContextShape>({
  myFeatures: storedFeatures,
  allFeatures: { ...featureDefaults, ...storedFeatures },
  setFeature: () => {},
  resetFeature: () => {},
});

export function FeatureProvider({ children }) {
  const [myFeatures, setFeatures] = useState<Partial<FeatureMap>>(
    storedFeatures
  );

  useEffect(() => {
    // Don't store on mount, only on change
    if (storedFeatures !== myFeatures) {
      const durationDays = isLocalDev() ? 365 : 1 / 24;
      setCookie(COOKIE_NAME, JSON.stringify(myFeatures), durationDays);
    }
  }, [myFeatures]);

  const resetFeature = useCallback(
    (key: FeatureName) => {
      let updatedFeatures = { ...myFeatures };
      delete updatedFeatures[key];
      setFeatures(updatedFeatures);
    },
    [myFeatures]
  );

  const setFeature = useCallback(
    (key: FeatureName, value: FeatureValue) => {
      if (value === featureDefaults[key]) {
        resetFeature(key);
      } else {
        setFeatures({ ...myFeatures, [key]: value });
      }
    },
    [myFeatures, resetFeature]
  );

  const memoizedValue = useMemo(
    () => ({
      myFeatures,
      allFeatures: { ...featureDefaults, ...myFeatures },
      setFeature,
      resetFeature,
    }),
    [myFeatures, setFeature, resetFeature]
  );

  return (
    <FeatureContext.Provider value={memoizedValue}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeature(feature: FeatureName): FeatureValue {
  const ctx = useContext(FeatureContext);
  return ctx.allFeatures[feature];
}

export function useFeatureEditing() {
  const ctx = useContext(FeatureContext);
  return useMemo(
    () => ({
      setFeature: ctx.setFeature,
      resetFeature: ctx.resetFeature,
      myFeatures: ctx.myFeatures,
    }),
    [ctx]
  );
}
